import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Combobox } from '../components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  MapPin, Clock, Check, User, Loader2, AlertTriangle,
  MapPinCheck, LogIn, LogOut, Calendar, Navigation, Eye, Trash2, Camera, UserX, Activity
} from 'lucide-react';
import { toast } from 'sonner';

// Haversine distance in meters (for client-side GPS pre-validation)
function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const MAX_DISTANCE = 500; // meters

const PROCEDIMENTOS_OPTIONS = [
  { key: 'sinais_vitais', label: 'Sinais Vitais' },
  { key: 'medicacoes', label: 'Medicações', hasQuantity: true },
  { key: 'exames_laboratoriais', label: 'Exames Laboratoriais', hasQuantity: true },
  { key: 'curativo', label: 'Curativo' },
  { key: 'intercorrencia', label: 'Intercorrência' },
  { key: 'visita_fisioterapeuta', label: 'Visita Fisioterapeuta' },
  { key: 'visita_medica', label: 'Visita Médica' },
  { key: 'visita_fonoaudiologo', label: 'Visita Fonoaudiólogo' },
  { key: 'visita_nutricionista', label: 'Visita Nutricionista' },
  { key: 'visita_psicologa', label: 'Visita Psicóloga' },
];

const Visitas = () => {
  const { api, user, can } = useAuth();
  const isAdmin = can('ver_checkins_todos');
  const isMasterAdmin = can('excluir_checkin');
  const canCheckin = can('criar_checkin');
  const canSeeTime = ['master_admin', 'enfermeiro'].includes((user?.perfil || '').toLowerCase());

  // State
  const [pacientes, setPacientes] = useState([]);
  const [visitas, setVisitas] = useState({ visitas: [], resumo: {} });
  const [minhasVisitas, setMinhasVisitas] = useState({ visitas: [] });
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

  // Check-in modal
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinForm, setCheckinForm] = useState({
    internacao_id: '',
    procedimentos_planejados: [],
    quantidade_medicacoes: '',
    quantidade_exames: '',
    justificativa_fora_raio: '',
    paciente_ausente: false,
    paciente_ausente_obs: '',
  });
  const [gpsStatus, setGpsStatus] = useState('idle');
  const [coords, setCoords] = useState(null);
  const [savingCheckin, setSavingCheckin] = useState(false);
  const [ausenteFotos, setAusenteFotos] = useState([]);

  // Detail modal
  const [showDetail, setShowDetail] = useState(false);
  const [detailVisita, setDetailVisita] = useState(null);
  const [tecnicoFilter, setTecnicoFilter] = useState('');
  const [perfilFilter, setPerfilFilter] = useState('');

  const PERFIL_TAGS = [
    { value: 'tecnico', label: 'Técnico', color: 'border-gray-400 bg-gray-50 text-gray-700' },
    { value: 'fisioterapeuta', label: 'Fisio', color: 'border-green-400 bg-green-50 text-green-700' },
    { value: 'fonoaudiologo', label: 'Fono', color: 'border-purple-400 bg-purple-50 text-purple-700' },
    { value: 'nutricionista', label: 'Nutri', color: 'border-orange-400 bg-orange-50 text-orange-700' },
    { value: 'psicologo', label: 'Psicólogo', color: 'border-blue-400 bg-blue-50 text-blue-700' },
    { value: 'medico', label: 'Clínico', color: 'border-rose-400 bg-rose-50 text-rose-700' },
    { value: 'enfermeiro', label: 'Enfermeiro', color: 'border-pink-400 bg-pink-50 text-pink-700' },
  ];

  // Extract unique professionals from visits, grouped by profile
  const profissionaisPorPerfil = useMemo(() => {
    const map = {};
    (visitas.visitas || []).forEach(v => {
      if (v.tecnico_nome && v.tecnico_id) {
        const perfil = v.tecnico_perfil || 'tecnico';
        if (!map[perfil]) map[perfil] = {};
        map[perfil][v.tecnico_id] = v.tecnico_nome;
      }
    });
    const result = {};
    for (const [perfil, profs] of Object.entries(map)) {
      result[perfil] = Object.entries(profs)
        .map(([id, nome]) => ({ id, nome }))
        .sort((a, b) => a.nome.localeCompare(b.nome));
    }
    return result;
  }, [visitas.visitas]);

  // Professionals to show in dropdown based on selected tag
  const profissionaisDropdown = useMemo(() => {
    if (!perfilFilter) {
      // Show all professionals
      const map = {};
      (visitas.visitas || []).forEach(v => {
        if (v.tecnico_nome && v.tecnico_id) {
          map[v.tecnico_id] = v.tecnico_nome;
        }
      });
      return Object.entries(map).map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
    }
    return profissionaisPorPerfil[perfilFilter] || [];
  }, [visitas.visitas, perfilFilter, profissionaisPorPerfil]);

  // Filtered visits
  const visitasFiltradas = useMemo(() => {
    let result = visitas.visitas || [];
    if (perfilFilter) {
      result = result.filter(v => (v.tecnico_perfil || 'tecnico') === perfilFilter);
    }
    if (tecnicoFilter) {
      result = result.filter(v => v.tecnico_id === tecnicoFilter);
    }
    return result;
  }, [visitas.visitas, tecnicoFilter, perfilFilter]);

  // Recalculate summary based on filter
  const resumoFiltrado = useMemo(() => {
    if (!tecnicoFilter && !perfilFilter) return visitas.resumo || {};
    const filtered = visitasFiltradas;
    return {
      total: filtered.length,
      em_andamento: filtered.filter(v => v.status === 'em_andamento').length,
      concluidas: filtered.filter(v => v.status === 'concluida').length,
      fora_do_raio: filtered.filter(v => v.fora_do_raio).length,
    };
  }, [visitasFiltradas, tecnicoFilter, perfilFilter, visitas.resumo]);

  // Fetch data
  const fetchPacientes = useCallback(async () => {
    try {
      const res = await api.get('/visitas/pacientes-internados');
      setPacientes(res.data || []);
    } catch { /* ignore */ }
  }, [api]);

  const fetchVisitas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/visitas?data_inicio=${dateFrom}&data_fim=${dateTo}`);
      setVisitas(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [api, dateFrom, dateTo]);

  const fetchMinhas = useCallback(async () => {
    try {
      const res = await api.get('/visitas/minhas');
      setMinhasVisitas(res.data);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchPacientes(); fetchMinhas(); }, [fetchPacientes, fetchMinhas]);
  useEffect(() => { fetchVisitas(); }, [fetchVisitas]);

  // GPS
  const requestGPS = () => {
    setGpsStatus('loading');
    if (!navigator.geolocation) {
      setGpsStatus('error');
      toast.error('Geolocalização não suportada neste navegador');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsStatus('success');
      },
      () => {
        setGpsStatus('error');
        toast.error('Não foi possível obter sua localização. Verifique as permissões do navegador.');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // Check if currently selected patient is out of GPS range
  const selectedPaciente = useMemo(() => {
    return pacientes.find(p => p.internacao_id === checkinForm.internacao_id);
  }, [pacientes, checkinForm.internacao_id]);

  const isForaDoRaio = useMemo(() => {
    if (!coords || !selectedPaciente?.lat || !selectedPaciente?.lng) return false;
    const dist = haversineDistance(coords.lat, coords.lng, selectedPaciente.lat, selectedPaciente.lng);
    return dist > MAX_DISTANCE;
  }, [coords, selectedPaciente]);

  const toggleProcedimento = (key) => {
    setCheckinForm(f => {
      const isSelected = f.procedimentos_planejados.includes(key);
      const newProcs = isSelected
        ? f.procedimentos_planejados.filter(k => k !== key)
        : [...f.procedimentos_planejados, key];
      const updates = { procedimentos_planejados: newProcs };
      // Clear quantity fields when unchecked
      if (isSelected && key === 'medicacoes') updates.quantidade_medicacoes = '';
      if (isSelected && key === 'exames_laboratoriais') updates.quantidade_exames = '';
      return { ...f, ...updates };
    });
  };

  const handleOpenCheckin = () => {
    setCheckinForm({
      internacao_id: '',
      procedimentos_planejados: [],
      quantidade_medicacoes: '',
      quantidade_exames: '',
      justificativa_fora_raio: '',
      paciente_ausente: false,
      paciente_ausente_obs: '',
    });
    setAusenteFotos([]);
    setCoords(null);
    setGpsStatus('idle');
    setShowCheckin(true);
    requestGPS();
  };

  const handleCheckin = async () => {
    if (!checkinForm.internacao_id) { toast.error('Selecione um paciente'); return; }
    if (!coords) { toast.error('Aguarde a localização GPS'); return; }
    if (isForaDoRaio && !checkinForm.justificativa_fora_raio.trim()) {
      toast.error('Informe a justificativa por estar fora do raio GPS');
      return;
    }
    setSavingCheckin(true);
    try {
      await api.post('/visitas/check-in', {
        internacao_id: checkinForm.internacao_id,
        lat: coords.lat,
        lng: coords.lng,
        procedimentos_planejados: checkinForm.procedimentos_planejados,
        quantidade_medicacoes: checkinForm.quantidade_medicacoes ? parseInt(checkinForm.quantidade_medicacoes) : null,
        quantidade_exames: checkinForm.quantidade_exames ? parseInt(checkinForm.quantidade_exames) : null,
        justificativa_fora_raio: isForaDoRaio ? checkinForm.justificativa_fora_raio.trim() : null,
        paciente_ausente: checkinForm.paciente_ausente || false,
        paciente_ausente_obs: checkinForm.paciente_ausente ? (checkinForm.paciente_ausente_obs?.trim() || '') : null,
        paciente_ausente_fotos: checkinForm.paciente_ausente ? ausenteFotos.map(f => f.data) : null,
      });
      toast.success('Check-in realizado com sucesso!');
      setShowCheckin(false);
      fetchMinhas();
      fetchVisitas();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao realizar check-in');
    } finally { setSavingCheckin(false); }
  };

  const formatTime = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); }
    catch { return '-'; }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try { return new Date(iso).toLocaleDateString('pt-BR'); }
    catch { return '-'; }
  };

  const handleDeleteVisita = async (e, visitaId) => {
    e.stopPropagation();
    if (!confirm('Tem certeza que deseja excluir esta visita?')) return;
    try {
      await api.delete(`/visitas/${visitaId}`);
      toast.success('Visita excluída');
      fetchVisitas();
      fetchMinhas();
    } catch {
      toast.error('Erro ao excluir visita');
    }
  };

  // Combobox options: meu_paciente first, then alphabetical (backend already sorts)
  const pacienteOptions = useMemo(() => {
    return pacientes.map(p => ({
      value: p.internacao_id,
      label: p.paciente_nome,
      subtitle: p.meu_paciente
        ? '⭐ Meu paciente — ' + (p.endereco || (p.tem_gps ? 'GPS disponível' : 'Sem GPS'))
        : (p.endereco || (p.tem_gps ? 'GPS disponível' : 'Sem GPS')),
    }));
  }, [pacientes]);

  return (
    <Layout>
      <div className="space-y-4 p-4 sm:p-6" data-testid="visitas-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Check-in Pacientes</h1>
            <p className="text-muted-foreground">Check-in de presença nas visitas domiciliares</p>
          </div>
          {canCheckin && (
          <Button
            onClick={handleOpenCheckin}
            className="bg-green-600 hover:bg-green-700 gap-2"
            data-testid="btn-checkin"
          >
            <LogIn className="h-4 w-4" />
            Novo Check-in
          </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue={isAdmin ? "painel" : "minhas"} className="space-y-4">
          <TabsList>
            <TabsTrigger value="minhas" data-testid="tab-minhas">
              <User className="h-4 w-4 mr-2" />
              Minhas Visitas ({minhasVisitas.visitas?.length || 0})
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="painel" data-testid="tab-painel">
                <Eye className="h-4 w-4 mr-2" />
                Painel Geral
              </TabsTrigger>
            )}
          </TabsList>

          {/* Minhas Visitas */}
          <TabsContent value="minhas" className="space-y-3">
            {minhasVisitas.visitas?.length > 0 ? (
              minhasVisitas.visitas.map((v) => (
                <Card key={v.id} className="cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => { setDetailVisita(v); setShowDetail(true); }}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                          v.status === 'em_andamento' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'
                        }`}>
                          <MapPin className={`h-4 w-4 ${v.status === 'em_andamento' ? 'text-green-600' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{v.paciente_nome}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            {canSeeTime && <span className="flex items-center gap-1"><LogIn className="h-3 w-3" />{formatTime(v.check_in_at)}</span>}
                            {v.duracao_minutos != null && <span>{v.duracao_minutos} min</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!v.gps_validado && <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">Fora do raio</Badge>}
                        <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">
                          Concluída
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <MapPin className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma visita registrada hoje</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Painel Geral (Admin) */}
          {isAdmin && (
            <TabsContent value="painel" className="space-y-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="space-y-1 w-44">
                      <Label className="text-sm flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> De</Label>
                      <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
                    </div>
                    <div className="space-y-1 w-44">
                      <Label className="text-sm flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Até</Label>
                      <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
                    </div>
                    <div className="flex items-end pt-5">
                      <Button variant="outline" size="sm" onClick={fetchVisitas} data-testid="btn-atualizar-visitas">Atualizar</Button>
                    </div>
                  </div>
                  {/* Profile tags + Professional dropdown */}
                  <div className="flex items-center gap-3 flex-wrap mt-3 pt-3 border-t">
                    <div className="flex items-center gap-1.5 flex-wrap" data-testid="filter-perfil-tags">
                      <button
                        onClick={() => { setPerfilFilter(''); setTecnicoFilter(''); }}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                          !perfilFilter ? 'bg-foreground text-background border-foreground' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                        }`}
                        data-testid="filter-perfil-todos"
                      >Todos</button>
                      {PERFIL_TAGS.map(tag => (
                        <button key={tag.value}
                          onClick={() => { setPerfilFilter(perfilFilter === tag.value ? '' : tag.value); setTecnicoFilter(''); }}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                            perfilFilter === tag.value ? tag.color : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                          }`}
                          data-testid={`filter-perfil-${tag.value}`}
                        >{tag.label}</button>
                      ))}
                    </div>
                    {/* Professional dropdown (filtered by selected tag) */}
                    {profissionaisDropdown.length > 0 && (
                      <div className="flex items-center gap-2" data-testid="filter-profissional-wrapper">
                        <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <select
                          value={tecnicoFilter}
                          onChange={(e) => setTecnicoFilter(e.target.value)}
                          className="flex h-8 rounded-md border border-input bg-transparent px-3 py-1 text-xs shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-w-[200px]"
                          data-testid="filter-profissional"
                        >
                          <option value="">
                            {perfilFilter
                              ? `Todos ${PERFIL_TAGS.find(t => t.value === perfilFilter)?.label || ''}s`
                              : 'Todos os profissionais'}
                          </option>
                          {profissionaisDropdown.map(p => (
                            <option key={p.id} value={p.id}>{p.nome}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Summary cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold">{resumoFiltrado?.total || 0}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{resumoFiltrado?.em_andamento || 0}</p>
                  <p className="text-xs text-muted-foreground">Em andamento</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{resumoFiltrado?.concluidas || 0}</p>
                  <p className="text-xs text-muted-foreground">Concluídas</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{resumoFiltrado?.fora_do_raio || 0}</p>
                  <p className="text-xs text-muted-foreground">Fora do raio GPS</p>
                </CardContent></Card>
              </div>

              {/* List */}
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : visitasFiltradas?.length > 0 ? (
                <div className="space-y-2">
                  {visitasFiltradas.map((v) => (
                    <Card key={v.id} className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => { setDetailVisita(v); setShowDetail(true); }}>
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                              v.status === 'em_andamento' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-800'
                            }`}>
                              <MapPin className={`h-4 w-4 ${v.status === 'em_andamento' ? 'text-green-600' : 'text-gray-500'}`} />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{v.paciente_nome}</p>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                                <span className="flex items-center gap-1"><User className="h-3 w-3" />{v.tecnico_nome}</span>
                                {canSeeTime && <span className="flex items-center gap-1"><LogIn className="h-3 w-3" />{formatTime(v.check_in_at)}</span>}
                                {canSeeTime && v.duracao_minutos != null && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{v.duracao_minutos} min</span>}
                                {v.distancia_metros != null && <span className="flex items-center gap-1"><Navigation className="h-3 w-3" />{v.distancia_metros}m</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {isMasterAdmin && (
                              <button
                                onClick={(e) => handleDeleteVisita(e, v.id)}
                                className="p-1.5 rounded-md hover:bg-red-100 text-red-500 hover:text-red-700 transition-colors"
                                title="Excluir visita"
                                data-testid={`btn-delete-visita-${v.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                            {!v.gps_validado && <Badge variant="outline" className="text-xs border-amber-400 text-amber-600"><AlertTriangle className="h-3 w-3 mr-1" />Fora do raio</Badge>}
                            <Badge variant="outline" className="text-xs border-gray-300">
                              Concluída
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card><CardContent className="py-8 text-center text-muted-foreground">
                  <MapPin className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma visita registrada nesta data</p>
                </CardContent></Card>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Check-in Modal */}
        <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-700">
                <LogIn className="h-5 w-5" />
                Novo Check-in
              </DialogTitle>
              <DialogDescription>Registre sua chegada na casa do paciente.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* GPS Status */}
              <div className={`flex items-center gap-3 p-3 rounded-lg border ${
                gpsStatus === 'success' ? 'bg-green-50 border-green-300' :
                gpsStatus === 'error' ? 'bg-red-50 border-red-300' :
                gpsStatus === 'loading' ? 'bg-blue-50 border-blue-300' :
                'bg-gray-50 border-gray-200'
              }`}>
                {gpsStatus === 'loading' && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
                {gpsStatus === 'success' && <MapPinCheck className="h-5 w-5 text-green-600" />}
                {gpsStatus === 'error' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                {gpsStatus === 'idle' && <MapPin className="h-5 w-5 text-gray-400" />}
                <div className="text-sm">
                  {gpsStatus === 'loading' && 'Obtendo localização GPS...'}
                  {gpsStatus === 'success' && `Localização obtida (${coords?.lat.toFixed(5)}, ${coords?.lng.toFixed(5)})`}
                  {gpsStatus === 'error' && (
                    <span>Erro ao obter GPS. <button className="underline text-blue-600" onClick={requestGPS}>Tentar novamente</button></span>
                  )}
                  {gpsStatus === 'idle' && 'Aguardando localização...'}
                </div>
              </div>

              {/* Patient selector */}
              <div className="space-y-2">
                <Label>Paciente *</Label>
                <Combobox
                  options={pacienteOptions}
                  value={checkinForm.internacao_id}
                  onValueChange={(v) => setCheckinForm(f => ({ ...f, internacao_id: v, justificativa_fora_raio: '' }))}
                  placeholder="Selecionar paciente..."
                  searchPlaceholder="Buscar..."
                  emptyText="Nenhum paciente encontrado"
                  data-testid="select-checkin-paciente"
                />
              </div>

              {/* Out-of-range warning + justification */}
              {isForaDoRaio && (
                <div className="space-y-2 p-3 rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-950/20">
                  <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Você está fora do raio permitido deste paciente
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm text-amber-800">Justificativa *</Label>
                    <Textarea
                      value={checkinForm.justificativa_fora_raio}
                      onChange={(e) => setCheckinForm(f => ({ ...f, justificativa_fora_raio: e.target.value }))}
                      placeholder="Informe o motivo de estar fora do raio GPS..."
                      rows={2}
                      className="resize-none border-amber-300"
                      data-testid="textarea-justificativa-fora-raio"
                    />
                  </div>
                </div>
              )}

              {/* Procedures */}
              <div className="space-y-2">
                <Label>O que será realizado *</Label>
                <div className="grid grid-cols-1 gap-2">
                  {PROCEDIMENTOS_OPTIONS.map(opt => {
                    const isSelected = checkinForm.procedimentos_planejados.includes(opt.key);
                    return (
                      <div key={opt.key}>
                        <label
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-green-50 border-green-400 dark:bg-green-950/20'
                              : 'bg-card hover:bg-accent/50'
                          }`}
                          data-testid={`checkin-proc-${opt.key}`}
                        >
                          <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 ${
                            isSelected ? 'bg-green-600 border-green-600' : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <span className="text-sm">{opt.label}</span>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={isSelected}
                            onChange={() => toggleProcedimento(opt.key)}
                          />
                        </label>
                        {/* Quantity field for Medicações */}
                        {opt.key === 'medicacoes' && isSelected && (
                          <div className="ml-8 mt-2 flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground whitespace-nowrap">Qtd medicações:</Label>
                            <Input
                              type="number" min="1" step="1"
                              value={checkinForm.quantidade_medicacoes}
                              onChange={(e) => setCheckinForm(f => ({ ...f, quantidade_medicacoes: e.target.value }))}
                              placeholder="0"
                              className="h-8 w-24"
                              data-testid="input-qtd-medicacoes"
                            />
                          </div>
                        )}
                        {/* Quantity field for Exames */}
                        {opt.key === 'exames_laboratoriais' && isSelected && (
                          <div className="ml-8 mt-2 flex items-center gap-2">
                            <Label className="text-xs text-muted-foreground whitespace-nowrap">Qtd exames:</Label>
                            <Input
                              type="number" min="1" step="1"
                              value={checkinForm.quantidade_exames}
                              onChange={(e) => setCheckinForm(f => ({ ...f, quantidade_exames: e.target.value }))}
                              placeholder="0"
                              className="h-8 w-24"
                              data-testid="input-qtd-exames"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Paciente ausente */}
              <div className="space-y-3">
                <label
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    checkinForm.paciente_ausente
                      ? 'bg-orange-50 border-orange-400 dark:bg-orange-950/20'
                      : 'bg-card hover:bg-accent/50'
                  }`}
                  data-testid="checkin-paciente-ausente"
                >
                  <div className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 ${
                    checkinForm.paciente_ausente ? 'bg-orange-600 border-orange-600' : 'border-gray-300'
                  }`}>
                    {checkinForm.paciente_ausente && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <UserX className="h-4 w-4 text-orange-600" />
                  <span className="text-sm">Paciente ausente no momento da visita domiciliar</span>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={checkinForm.paciente_ausente}
                    onChange={() => {
                      setCheckinForm(f => ({ ...f, paciente_ausente: !f.paciente_ausente, paciente_ausente_obs: '' }));
                      if (checkinForm.paciente_ausente) setAusenteFotos([]);
                    }}
                  />
                </label>

                {checkinForm.paciente_ausente && (
                  <div className="ml-2 space-y-3 p-3 rounded-lg border border-orange-300 bg-orange-50/50 dark:bg-orange-950/10">
                    <div className="space-y-1">
                      <Label className="text-sm text-orange-800">Observação</Label>
                      <Textarea
                        value={checkinForm.paciente_ausente_obs}
                        onChange={(e) => setCheckinForm(f => ({ ...f, paciente_ausente_obs: e.target.value }))}
                        placeholder="Descreva as circunstâncias da ausência..."
                        rows={2}
                        className="resize-none border-orange-200"
                        data-testid="textarea-paciente-ausente-obs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm text-orange-800 flex items-center gap-1">
                        <Camera className="h-3.5 w-3.5" />
                        Fotos de registro ({ausenteFotos.length}/4)
                      </Label>
                      <div className="grid grid-cols-4 gap-2">
                        {ausenteFotos.map((foto, idx) => (
                          <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border group">
                            <img src={foto.data} alt={foto.name} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => setAusenteFotos(prev => prev.filter((_, i) => i !== idx))}
                              className="absolute top-1 right-1 h-5 w-5 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                        {ausenteFotos.length < 4 && (
                          <label className="aspect-square rounded-lg border-2 border-dashed border-orange-300 hover:border-orange-500 cursor-pointer flex flex-col items-center justify-center text-muted-foreground hover:text-orange-600 transition-colors">
                            <Camera className="h-5 w-5 mb-1" />
                            <span className="text-xs">Adicionar</span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                if (ausenteFotos.length + files.length > 4) {
                                  toast.error('Máximo de 4 fotos permitidas');
                                  return;
                                }
                                files.forEach(file => {
                                  if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} excede 5MB`); return; }
                                  const reader = new FileReader();
                                  reader.onload = (ev) => {
                                    setAusenteFotos(prev => [...prev.slice(0, 3), { name: file.name, data: ev.target.result }]);
                                  };
                                  reader.readAsDataURL(file);
                                });
                                e.target.value = '';
                              }}
                              className="hidden"
                              data-testid="input-paciente-ausente-foto"
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCheckin(false)}>Cancelar</Button>
              <Button
                onClick={handleCheckin}
                disabled={savingCheckin || !checkinForm.internacao_id || gpsStatus !== 'success' || (isForaDoRaio && !checkinForm.justificativa_fora_raio.trim())}
                className="bg-green-600 hover:bg-green-700"
                data-testid="btn-confirmar-checkin"
              >
                {savingCheckin ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                Confirmar Check-in
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Detalhes da Visita</DialogTitle>
            </DialogHeader>
            {detailVisita && (
              <div className="space-y-3 py-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Paciente:</span><p className="font-medium">{detailVisita.paciente_nome}</p></div>
                  <div><span className="text-muted-foreground">Técnico:</span><p className="font-medium">{detailVisita.tecnico_nome}</p></div>
                  <div><span className="text-muted-foreground">Check-in:</span><p className="font-medium">{canSeeTime ? `${formatTime(detailVisita.check_in_at)} — ` : ''}{formatDate(detailVisita.check_in_at)}</p></div>
                  <div>
                    <span className="text-muted-foreground">GPS:</span>
                    <p className="font-medium">
                      {detailVisita.gps_validado
                        ? <Badge className="bg-green-100 text-green-700">Validado{detailVisita.distancia_metros != null && ` (${detailVisita.distancia_metros}m)`}</Badge>
                        : <Badge className="bg-amber-100 text-amber-700">Fora do raio{detailVisita.distancia_metros != null && ` (${detailVisita.distancia_metros}m)`}</Badge>
                      }
                    </p>
                  </div>
                </div>
                {detailVisita.paciente_endereco && (
                  <div className="text-sm"><span className="text-muted-foreground">Endereço:</span><p>{detailVisita.paciente_endereco}</p></div>
                )}
                {detailVisita.procedimentos_planejados?.length > 0 && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Procedimentos planejados:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {detailVisita.procedimentos_planejados.map(p => {
                        const opt = PROCEDIMENTOS_OPTIONS.find(o => o.key === p);
                        return <Badge key={p} variant="outline" className="text-xs">{opt?.label || p}</Badge>;
                      })}
                    </div>
                  </div>
                )}
                {detailVisita.quantidade_medicacoes != null && detailVisita.quantidade_medicacoes > 0 && (
                  <div className="text-sm"><span className="text-muted-foreground">Qtd medicações:</span><p className="font-medium">{detailVisita.quantidade_medicacoes}</p></div>
                )}
                {detailVisita.quantidade_exames != null && detailVisita.quantidade_exames > 0 && (
                  <div className="text-sm"><span className="text-muted-foreground">Qtd exames:</span><p className="font-medium">{detailVisita.quantidade_exames}</p></div>
                )}
                {detailVisita.justificativa_fora_raio && (
                  <div className="text-sm p-2 rounded-lg bg-amber-50 border border-amber-200">
                    <span className="text-amber-700 font-medium">Justificativa fora do raio:</span>
                    <p className="text-amber-800">{detailVisita.justificativa_fora_raio}</p>
                  </div>
                )}
                {detailVisita.paciente_ausente && (
                  <div className="text-sm p-2 rounded-lg bg-orange-50 border border-orange-200">
                    <div className="flex items-center gap-1 text-orange-700 font-medium mb-1">
                      <UserX className="h-4 w-4" /> Paciente ausente
                    </div>
                    {detailVisita.paciente_ausente_obs && (
                      <p className="text-orange-800 mb-2">{detailVisita.paciente_ausente_obs}</p>
                    )}
                    {detailVisita.paciente_ausente_fotos?.length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        {detailVisita.paciente_ausente_fotos.map((foto, idx) => (
                          <div key={idx} className="aspect-square rounded-lg overflow-hidden border">
                            <img src={foto} alt={`Registro ${idx + 1}`} className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {detailVisita.observacoes_checkin && (
                  <div className="text-sm"><span className="text-muted-foreground">Obs check-in:</span><p>{detailVisita.observacoes_checkin}</p></div>
                )}
                {detailVisita.procedimentos && (
                  <div className="text-sm"><span className="text-muted-foreground">Procedimentos realizados:</span><p>{detailVisita.procedimentos}</p></div>
                )}
                {detailVisita.observacoes_checkout && (
                  <div className="text-sm"><span className="text-muted-foreground">Obs check-out:</span><p>{detailVisita.observacoes_checkout}</p></div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetail(false)}>Fechar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Visitas;
